import { ChangeEvent, use, useEffect, useState } from "react";
import { User } from "../models/Users/User";
import agent from "../../api/agent";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import {
  Stack,
  Typography,
  Tooltip,
  Fab,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  TablePagination,
  Divider,
  TableSortLabel,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import { UserCreate } from "../models/Users/UserCreate";
import CreateUserForm from "../forms/Users/CreateUserForm";
import EditUserForm from "../forms/Users/EditUserForm";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";
import LockResetIcon from "@mui/icons-material/LockReset";
import ResetPassword from "../models/Users/ResetPassword";
import PasswordResetForm from "../forms/Users/PasswordResetForm";
import LicenseByUser from "../details/LicenseByUser";

export default function UserDashboard() {
  const [userList, setUserList] = useState<User[]>([]);
  const [filteredUserList, setFilteredUserList] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [createFormOpenState, setCreateFormOpenState] =
    useState<boolean>(false);
  const [editFormOpenState, setEditFormOpenState] = useState<boolean>(false);
  const [deleteDialogOpenState, setDeleteDialogOpenState] =
    useState<boolean>(false);

  const [selectedUser, setSelectedUser] = useState<UserCreate | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState<
    "companyName" | "email" | "userName" | "role"
  >("companyName");

  //For User password reset
  const [pwResetFormOpenState, setPwResetFormOpenState] =
    useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<string>("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Authentication
  const [token, setToken] = useState<string>("");

  // Show License Details by User
  const [licenseDetailsFormOpenState, setLicenseDetailsFormOpenState] =
    useState<boolean>(false);
  const [selectedUserIdForLicense, setSelectedUserIdForLicense] = useState<
    number | undefined
  >(undefined);

  // For table sorting
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<
    "email" | "userName" | "role" | "licenseCount"
  >("email");

  // For export to CSV
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          setToken(tokenFLocal);

          // get the data
          const userResponse = await agent.User.list(tokenFLocal);
          setUserList(userResponse);
          setFilteredUserList(userResponse);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  function handleCreateFormOpen() {
    setCreateFormOpenState(true);
  }

  function handleEditFormOpen(userId: number) {
    const getUser = userList.find((x) => x.userId === userId);
    if (getUser) {
      // Map User to UserCreate
      const userCreate: UserCreate = {
        email: getUser.email,
        userName: getUser.userName,
        phoneNumber: getUser.phoneNumber,
        role: getUser.role,
        companyId: getUser.companyId,
        // Add other properties as needed
      };
      setSelectedUser(userCreate);
      setSelectedUserId(userId);
      setEditFormOpenState(true);
    }
  }

  function handleCreateOrEditFormClose() {
    setCreateFormOpenState(false);
    setEditFormOpenState(false);
    setSelectedUser(null);
    setSelectedUserId(0);
  }

  function handleDeleteOnClick(userId: number) {
    setSelectedUserId(userId);
    setDeleteDialogOpenState(true);
  }

  function handleDeleteDialogClose() {
    setSelectedUserId(0);
    setDeleteDialogOpenState(false);
  }

  // Function to create new User
  async function handleCreateSubmit(formData: UserCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.create(formData, token).then(async () => {
        const updatedUser = await agent.User.list(token);
        setUserList(updatedUser);
        handleCreateOrEditFormClose();
        showToast("User Created Successfully", "success");
      });
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data;

        // Check for email already exists error
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Email already exists")
        ) {
          showToast("Email already exists", "error");
        }
        // Check for email domain validation error
        else if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Invalid Email domain")
        ) {
          showToast("Invalid email domain", "error");
        }
        // Check for general bad request with specific messages
        else if (typeof errorMessage === "string") {
          // Display whatever error message came from the backend
          showToast(errorMessage, "error");
        } else if (errorMessage.errors) {
          // Handle validation errors object (if backend returns this format)
          const firstError = Object.values(errorMessage.errors)[0] as string[];
          showToast(firstError?.[0] || "Validation error", "error");
        } else {
          // Fallback for other response formats
          showToast(errorMessage.message || "Failed to create user", "error");
        }
      } else {
        // Something else went wrong
        showToast("An unexpected error occurred", "error");
        console.error("Error setting up request:", error.message);
      }
    } finally {
      setLoading(false);
    }
  }

  // Function to edit User details
  async function handleEditSubmit(formData: UserCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.update(selectedUserId, formData, token).then(
        async () => {
          const updatedUsers = await agent.User.list(token);
          setUserList(updatedUsers);
          handleCreateOrEditFormClose();
          showToast("User Edited Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error updating user:", error);
      showToast("Error Updating user", "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedUserId === 0) return;
    setLoading(true);
    try {
      await agent.User.delete(selectedUserId, token).then(async () => {
        const updatedUser = await agent.User.list(token);
        setUserList(updatedUser);
        handleDeleteDialogClose();
        showToast("User Deleted Successfully", "success");
      });
    } catch (error) {
      console.error("Error deleting User:", error);
      showToast("Error deleting user", "error");
    } finally {
      setLoading(false);
      handleDeleteDialogClose(); //Reset all the delete form open state
    }
  }

  // Function for user Password Reset
  function handlePWResetFormOpen(email: string) {
    setSelectedEmail(email);
    setPwResetFormOpenState(true);
  }

  function handlePWResetFormClose() {
    setSelectedEmail("");
    setPwResetFormOpenState(false);
  }

  async function handlePWReset(formData: ResetPassword) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.resetPassword(selectedEmail, formData, token).then(
        () => {
          handlePWResetFormClose();
          showToast("User Password Reset Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error reset user Password:", error);
      showToast("Error Reset User Password", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function for View License Details by User
  function handleLicenseDetailsFormOpen(userId: number) {
    setSelectedUserIdForLicense(userId);
    setLicenseDetailsFormOpenState(true);
  }

  function handleLicenseDetailsFormClose() {
    setSelectedUserIdForLicense(undefined);
    setLicenseDetailsFormOpenState(false);
  }

  // Function for the search and filter
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredUserList(userList);
      return;
    }

    const filtered = userList.filter((user) =>
      String(user[searchColumn])
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    );
    setFilteredUserList(filtered);
  }, [searchTerm, searchColumn, userList]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleColumnChange = (event: any) => {
    setSearchColumn(event.target.value);
  };

  // Function for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // For table sorting function
  const handleSort = (
    property: "email" | "userName" | "role" | "licenseCount",
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getStringValue = (value: string | undefined): string => {
    return value?.toLowerCase() || "";
  };

  const getNumberValue = (value: number | undefined): number => {
    return value ?? 0; // Treat undefined as 0
  };

  const sortedUserData = [...filteredUserList].sort((a, b) => {
    const isAsc = order === "asc";

    switch (orderBy) {
      case "email":
        const nameA = getStringValue(a.email);
        const nameB = getStringValue(b.email);
        return isAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);

      case "userName":
        const emailA = getStringValue(a.userName);
        const emailB = getStringValue(b.userName);
        return isAsc
          ? emailA.localeCompare(emailB)
          : emailB.localeCompare(emailA);

      case "role":
        const roleA = getStringValue(a.role);
        const roleB = getStringValue(b.role);
        return isAsc ? roleA.localeCompare(roleB) : roleB.localeCompare(roleA);

      case "licenseCount":
        const licenseCountA = getNumberValue(a.licenseCount);
        const licenseCountB = getNumberValue(b.licenseCount);
        return isAsc
          ? licenseCountA - licenseCountB
          : licenseCountB - licenseCountA;

      default:
        return 0;
    }
  });

  // Function to export user list to CSV file
    const handleExport = async () => {
      if (checkTokenExpiration(token)) {
        return;
      }
      setExportLoading(true);
      try {
        const response = await agent.User.exportToCSV(token);
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        let filename = `user-export-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        showToast("Exported to CSV successfully", "success");
      } catch (error) {
        console.error("Error exporting to CSV:", error);
        showToast("Error exporting to CSV", "error");
      } finally {
        setExportLoading(false);
      }
    };

  const PaginationComponent = () => (
    <TablePagination
      rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
      component="div"
      count={filteredUserList.length} // Total number of rows
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      sx={{
        ".MuiTablePagination-toolbar": {
          paddingLeft: 2, // Adjust padding as needed
        },
      }}
    />
  );

  // trigger the loader, loading before the data is retrieved from database
  if (loading) return <LoadingComponent content="Loading Data" />;
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">User List</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Create New" followCursor>
            <Fab
              color="primary"
              onClick={() => handleCreateFormOpen()}
              size="small"
            >
              <AddIcon />
            </Fab>
          </Tooltip>

          <Tooltip title="Export to CSV" followCursor>
            <span>
              <Fab
                color="success"
                onClick={() => handleExport()}
                size="small"
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <GetAppIcon />
                )}
              </Fab>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2, // Add margin bottom if needed
        }}
      >
        {/* Left side - Search and Filter */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            label="Search"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <FormControl sx={{ ml: 1 }}>
            <InputLabel>Filter by</InputLabel>
            <Select
              value={searchColumn}
              label="Filter by"
              onChange={handleColumnChange}
            >
              <MenuItem value="companyName">Company Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="userName">Username</MenuItem>
              <MenuItem value="role">Role</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Right side - Pagination */}
        <PaginationComponent />
      </Box>

      {filteredUserList.length === 0 ? (
        <Box sx={{ mt: 5 }}>
          <Divider>
            <Typography variant="button">No Record found</Typography>
          </Divider>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "email"}
                      direction={orderBy === "email" ? order : "asc"}
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "userName"}
                      direction={orderBy === "userName" ? order : "asc"}
                      onClick={() => handleSort("userName")}
                    >
                      User Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "role"}
                      direction={orderBy === "role" ? order : "asc"}
                      onClick={() => handleSort("role")}
                    >
                      Roles
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <TableSortLabel
                      active={orderBy === "licenseCount"}
                      direction={orderBy === "licenseCount" ? order : "asc"}
                      onClick={() => handleSort("licenseCount")}
                    >
                      Number of Licenses Assigned
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUserData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                    <TableRow key={user.userId}>
                      <TableCell>{user.companyName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.userName}</TableCell>
                      <TableCell>{user.phoneNumber}</TableCell>
                      <TableCell>{user.role}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        {user.licenseCount}
                      </TableCell>
                      <TableCell>
                        {/* Button for View License Details */}
                        <Tooltip
                          title={
                            user.licenseCount === 0
                              ? "This user has no licenses"
                              : "View/Edit Licenses"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleLicenseDetailsFormOpen(user.userId)
                              }
                              disabled={user.licenseCount === 0}
                              sx={{
                                opacity: user.licenseCount === 0 ? 0.5 : 1,
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* Button for password reset */}
                        <Tooltip title="User Password Reset">
                          <IconButton
                            size="small"
                            onClick={() => handlePWResetFormOpen(user.email)}
                          >
                            <LockResetIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit User Details">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => handleEditFormOpen(user.userId)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteOnClick(user.userId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {/* Pagination Component (Company Table) */}
            <PaginationComponent />
          </TableContainer>
        </>
      )}

      {/* --------------------- User MODEL------------------------------- */}
      {/* Model for user create form */}
      <Dialog
        open={createFormOpenState}
        onClose={handleCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Create New User
          </Typography>
        </DialogTitle>
        <DialogContent>
          <CreateUserForm
            selectedCompanyId={undefined}
            closeForm={handleCreateOrEditFormClose}
            create={handleCreateSubmit}
            fromUserDashboard={true}
          />
        </DialogContent>
      </Dialog>

      {/* Model for user edit form */}
      <Dialog
        open={editFormOpenState}
        onClose={handleCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Edit User Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <EditUserForm
              selectedUser={selectedUser}
              closeForm={handleCreateOrEditFormClose}
              edit={handleEditSubmit}
              fromUserDashboard={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Model for user Password Reset form */}
      <Dialog
        open={pwResetFormOpenState}
        onClose={handlePWResetFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            User Password Reset
          </Typography>
        </DialogTitle>
        <DialogContent>
          <PasswordResetForm
            email={selectedEmail}
            closeForm={handlePWResetFormClose}
            passwordReset={handlePWReset}
          />
        </DialogContent>
      </Dialog>

      {/* Model for license details form */}
      <Dialog
        open={licenseDetailsFormOpenState}
        onClose={handleLicenseDetailsFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            ml: 2,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="h2">
            License Details
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleLicenseDetailsFormClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              "&:hover": {
                color: (theme) => theme.palette.grey[700],
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* {selectedUser && ( */}
          <LicenseByUser
            userIdForChecking={selectedUserIdForLicense}
            closeForm={handleLicenseDetailsFormClose}
          />
          {/* )} */}
        </DialogContent>
      </Dialog>

      <DeleteConfirmationBox
        open={deleteDialogOpenState}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
